import {useHistory} from "react-router-dom";
import React from "react";
import {Form, Formik} from "formik";
import clsx from "clsx";
import {FormikTextField} from "../form/FormikTextField";
import {CircularProgress, IconButton, makeStyles} from "@material-ui/core";
import * as yup from "yup";
import {LabeledTextDisplay, LabeledTextDisplayType} from "./LabeledTextDisplay";
import {useEditorLocation} from "../editor/editor.hook";
import {RedCloseIcon} from "./RedCloseIcon";
import {GreenCheckIcon} from "./GreenCheckIcon";

const useStyles = makeStyles(theme => ({
    spacing: {
        marginRight: theme.spacing(1)
    },
    container: {
        display: 'flex',
        alignItems: 'center'
    },
    select: {
        width: 100
    }
}));

interface TextFieldFormProps {
    label: string;
    required?: boolean;
    type?: LabeledTextDisplayType | "number";
    initialValue?: string | number;
    className?: string;
    onSubmit: (dto: string | number) => Promise<any>;
    suffix?: string;
    currency?: boolean;
    multiline?: boolean;
}

const validationSchemaMap = {
    email: yup.object({value: yup.string().typeError("Muss Email sein").required("Muss Email sein").email(('Email falsch formatiert')).required('benötigt')}),
    number: yup.object({value: yup.number().typeError('Muss positive Zahl sein').required('benötigt')})
}

function TextFieldForm({
                           initialValue,
                           className,
                           onSubmit,
                           type,
                           required,
                           label,
                           suffix,
                           currency,
                           multiline
                       }: TextFieldFormProps) {
    const classes = useStyles();
    const history = useHistory();
    const [isLoading, setIsLoading] = React.useState(false);

    const closeForm = () => history.replace({search: undefined});

    return (
        <Formik
            initialValues={{value: initialValue ?? ''}}
            onSubmit={(values) => {
                setIsLoading(true);
                onSubmit(type === "number" ? parseFloat(values.value as string) : values.value)
                    .catch(() => {
                    })
                    .finally(() => {
                        setIsLoading(false);
                        closeForm();
                    })
            }}
            validationSchema={
                // @ts-ignore
                validationSchemaMap[(type ?? '') as const]
            }
        >
            {({submitForm}) => (
                <Form className={clsx(className, classes.container)}>
                    <FormikTextField
                        multiline={multiline}
                        fullWidth
                        required={required}
                        type={type}
                        name={"value"}
                        label={label}
                        suffix={suffix}
                        currency={currency}
                    />
                    {isLoading
                        ? <CircularProgress size={20} color="secondary"/>
                        : (
                            <>
                                <IconButton edge="end" onClick={closeForm}>
                                    <RedCloseIcon/>
                                </IconButton>
                                <IconButton edge="end" onClick={submitForm}>
                                    <GreenCheckIcon/>
                                </IconButton>
                            </>
                        )
                    }
                </Form>
            )}
        </Formik>
    )
}

interface EditableTextFieldProps {
    formProps: TextFieldFormProps;
    editIdentifier: string;
    suffix?: string;
    currency?: boolean;
}

export function EditableTextField({formProps, editIdentifier, suffix, currency}: EditableTextFieldProps) {

    const {mode, reference} = useEditorLocation();
    const showForm = mode === 'edit' && reference === editIdentifier;


    return showForm
        ? (
            <TextFieldForm {...formProps} suffix={suffix} currency={currency}/>
        )
        : (
            <LabeledTextDisplay
                label={formProps.label}
                // @ts-ignore
                type={formProps.type}
                editIdentifier={editIdentifier}
                value={formProps.initialValue}
                className={formProps.className}
                suffix={suffix}
                currency={currency}
                required={formProps.required}
            />
        )
}
